import { Badge, Button, ConfirmDialog, EmptyState, Input, SearchInput, Select, Surface, Textarea, cn } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PortalAssortmentRow, SalesOrderDetail } from '@warehouse/shared';
import { CheckCircle2, Minus, Plus, ShoppingBasket, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useAuthUser } from '../../../auth/auth-store';
import { PageHeader } from '../../../components/PageHeader';
import { toastError, toastInfo } from '../../../lib/notify';
import { ORDER_QUERY_KEYS, portalApi } from '../api';
import { DeliveryCalendar } from '../components/DeliveryCalendar';
import { clearDraft, loadDraft, saveDraft } from './draft';

/**
 * `/portal` — the restaurant ordering screen (spec §5.13).
 *
 * Rules that are load-bearing here:
 *  - **No prices anywhere.** The API has none to send.
 *  - Quantities are entered in the SALES unit (kartong, kilo) with the base-unit
 *    conversion shown as a hint — the restaurant thinks in cartons.
 *  - The delivery-date calendar only offers dates the server would accept; the
 *    server checks again on submit, so a stale tab gets `E_ORDER_BY_CUTOFF`
 *    rendered as a localized message rather than a silent wrong delivery.
 *  - The basket survives a reload (draft.ts).
 */
export function PortalOrderPage(): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const user = useAuthUser();
  const queryClient = useQueryClient();

  const [restaurantId, setRestaurantId] = useState('');
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [deliveryDate, setDeliveryDate] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [placed, setPlaced] = useState<SalesOrderDetail | null>(null);
  const [draftRestored, setDraftRestored] = useState(false);

  const restaurantsQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.restaurants(),
    queryFn: () => portalApi.restaurants(),
  });

  // One restaurant is the normal case — select it without asking.
  useEffect(() => {
    const items = restaurantsQuery.data?.items ?? [];
    if (!restaurantId && items.length > 0) setRestaurantId(items[0]!.id);
  }, [restaurantsQuery.data, restaurantId]);

  const listState = useMemo(
    () => ({
      q: search.trim() || undefined,
      filters: categoryId ? { categoryId } : undefined,
      page: 1,
      pageSize: 200,
    }),
    [search, categoryId],
  );

  const assortmentQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.assortment(listState),
    queryFn: () => portalApi.assortment(listState),
  });
  const categoriesQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.categories(),
    queryFn: () => portalApi.categories(),
  });

  const articlesById = useMemo(() => {
    const map = new Map<string, PortalAssortmentRow>();
    for (const row of assortmentQuery.data?.items ?? []) map.set(row.articleId, row);
    return map;
  }, [assortmentQuery.data]);

  const basket = useMemo(
    () => Object.entries(quantities).filter(([, qty]) => Number(qty) > 0),
    [quantities],
  );

  // The categories in the basket drive the calendar: a restaurant with a
  // per-category schedule gets the right (most restrictive) set of dates.
  const basketCategoryIds = useMemo(
    () =>
      [
        ...new Set(
          basket
            .map(([articleId]) => articlesById.get(articleId)?.categoryId)
            .filter((id): id is string => Boolean(id)),
        ),
      ].sort(),
    [basket, articlesById],
  );

  const datesQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.deliveryDates(restaurantId, basketCategoryIds),
    queryFn: () => portalApi.deliveryDates(restaurantId, basketCategoryIds),
    enabled: restaurantId.length > 0,
  });

  // --- draft -------------------------------------------------------------
  useEffect(() => {
    if (!user || !restaurantId) return;
    const draft = loadDraft(user.id, restaurantId);
    if (!draft || draft.lines.length === 0) return;
    setQuantities(Object.fromEntries(draft.lines.map((l) => [l.articleId, l.qty])));
    setNote(draft.note);
    setDeliveryDate(draft.deliveryDate);
    setDraftRestored(true);
  }, [user, restaurantId]);

  useEffect(() => {
    if (!user || !restaurantId || placed) return;
    if (basket.length === 0) return;
    saveDraft(user.id, {
      restaurantId,
      deliveryDate,
      note,
      lines: basket.map(([articleId, qty]) => ({ articleId, qty })),
    });
  }, [user, restaurantId, basket, note, deliveryDate, placed]);

  useEffect(() => {
    if (draftRestored) {
      toastInfo(t('salesOrders:portal.cart.draftRestored'));
      setDraftRestored(false);
    }
  }, [draftRestored, t]);

  // --- submit ------------------------------------------------------------
  const createMutation = useMutation({
    mutationFn: () =>
      portalApi.createOrder({
        restaurantId,
        deliveryDate: deliveryDate ?? '',
        ...(note.trim() ? { note: note.trim() } : {}),
        lines: basket.map(([articleId, qty]) => ({
          articleId,
          qty,
          ...(articlesById.get(articleId)?.unitId ? { unitId: articlesById.get(articleId)!.unitId } : {}),
        })),
      }),
    onSuccess: (order) => {
      if (user) clearDraft(user.id, restaurantId);
      setPlaced(order);
      setQuantities({});
      setNote('');
      setDeliveryDate(null);
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
    },
    onError: (error: unknown) => {
      setConfirming(false);
      toastError(error);
    },
  });

  const restaurants = restaurantsQuery.data?.items ?? [];
  const restaurant = restaurants.find((r) => r.id === restaurantId);

  if (!restaurantsQuery.isLoading && restaurants.length === 0) {
    return (
      <>
        <PageHeader title={t('salesOrders:portal.title')} />
        <EmptyState icon={<ShoppingBasket />} title={t('salesOrders:portal.noRestaurant')} />
      </>
    );
  }

  if (placed) {
    return (
      <>
        <PageHeader title={t('salesOrders:portal.success.title')} />
        <Surface className="flex flex-col items-start gap-3 p-6" data-testid="order-success">
          <CheckCircle2 className="size-8 text-green-600" aria-hidden />
          <p className="text-lg">
            {t('salesOrders:portal.success.message', {
              orderNo: placed.orderNo,
              date: placed.deliveryDate,
            })}
          </p>
          <p className="text-sm text-ink-muted">{t('salesOrders:portal.success.emailNotice')}</p>
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/portal/orders/${placed.id}`}
              className="rounded-full bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
            >
              {t('salesOrders:portal.success.viewOrder')}
            </Link>
            <Button
              variant="secondary"
              onClick={() => {
                setPlaced(null);
              }}
            >
              {t('salesOrders:portal.success.newOrder')}
            </Button>
          </div>
        </Surface>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('salesOrders:portal.title')}
        subtitle={t('salesOrders:portal.subtitle')}
        actions={
          restaurants.length > 1 ? (
            <Select
              aria-label={t('salesOrders:portal.restaurant')}
              value={restaurantId}
              onChange={(event) => {
                setRestaurantId(event.target.value);
                setDeliveryDate(null);
              }}
              options={restaurants.map((r) => ({ value: r.id, label: r.name }))}
            />
          ) : restaurant ? (
            <Badge tone="neutral">{restaurant.name}</Badge>
          ) : null
        }
      />

      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput
              variant="outline"
              className="w-full sm:w-72"
              value={search}
              onChange={setSearch}
              onClear={() => {
                setSearch('');
              }}
              placeholder={t('salesOrders:portal.searchPlaceholder')}
              aria-label={t('salesOrders:portal.searchPlaceholder')}
              clearLabel={t('common:actions.clearFilters')}
            />
            <Select
              aria-label={t('salesOrders:portal.allCategories')}
              value={categoryId}
              onChange={(event) => {
                setCategoryId(event.target.value);
              }}
              options={[
                { value: '', label: t('salesOrders:portal.allCategories') },
                ...(categoriesQuery.data?.items ?? []).map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
          </div>

          <ul className="flex flex-col gap-2" data-testid="assortment">
            {(assortmentQuery.data?.items ?? []).map((article) => {
              const qty = quantities[article.articleId] ?? '';
              const inBasket = Number(qty) > 0;
              return (
                <li key={article.articleId}>
                  <Surface
                    className={cn('flex flex-wrap items-center gap-3 p-3', inBasket && 'ring-1 ring-brand-600/40')}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">
                        {article.name}
                        {article.nameZh ? <span className="ml-2 text-ink-muted">{article.nameZh}</span> : null}
                      </p>
                      <p className="text-xs text-ink-muted">
                        <span className="font-mono">{article.articleNo}</span>
                        {article.categoryName ? ` · ${article.categoryName}` : ''}
                        {' · '}
                        {t('salesOrders:portal.unitHint', {
                          unit: article.unitCode,
                          qty: trimQty(article.qtyInBase),
                          baseUnit: article.baseUnitCode,
                        })}
                      </p>
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`−1 ${article.articleNo}`}
                        onClick={() => {
                          setQuantities((current) => step(current, article.articleId, -1));
                        }}
                      >
                        <Minus className="size-4" aria-hidden />
                      </Button>
                      <Input
                        className="w-20"
                        type="number"
                        min="0"
                        step="1"
                        inputMode="decimal"
                        value={qty}
                        aria-label={`${t('salesOrders:portal.cart.qty')} ${article.name}`}
                        onChange={(event) => {
                          setQuantities((current) => ({ ...current, [article.articleId]: event.target.value }));
                        }}
                      />
                      <span className="w-10 text-xs text-ink-muted">{article.unitCode}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`+1 ${article.articleNo}`}
                        onClick={() => {
                          setQuantities((current) => step(current, article.articleId, 1));
                        }}
                      >
                        <Plus className="size-4" aria-hidden />
                      </Button>
                    </div>
                  </Surface>
                </li>
              );
            })}
          </ul>

          {!assortmentQuery.isLoading && (assortmentQuery.data?.items.length ?? 0) === 0 ? (
            <p className="text-sm text-ink-muted">{t('common:common.noResults')}</p>
          ) : null}
        </div>

        <Surface className="flex h-fit flex-col gap-4 p-5 lg:sticky lg:top-4" data-testid="cart">
          <h2 className="flex items-center gap-2 font-medium">
            <ShoppingBasket className="size-4" aria-hidden />
            {t('salesOrders:portal.cart.title')}
          </h2>

          {basket.length === 0 ? (
            <p className="text-sm text-ink-muted">{t('salesOrders:portal.cart.empty')}</p>
          ) : (
            <>
              <ul className="flex flex-col gap-1.5 text-sm">
                {basket.map(([articleId, qty]) => {
                  const article = articlesById.get(articleId);
                  return (
                    <li key={articleId} className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate">{article?.name ?? articleId}</span>
                      <span className="flex shrink-0 items-center gap-2 text-ink-muted">
                        {trimQty(qty)} {article?.unitCode}
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`${t('salesOrders:portal.cart.remove')} ${article?.name ?? ''}`}
                          onClick={() => {
                            setQuantities((current) => {
                              const next = { ...current };
                              delete next[articleId];
                              return next;
                            });
                          }}
                        >
                          <Trash2 className="size-3.5" aria-hidden />
                        </Button>
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div>
                <p className="mb-2 text-sm font-medium">{t('salesOrders:portal.cart.deliveryDate')}</p>
                <DeliveryCalendar
                  dates={datesQuery.data?.dates ?? []}
                  passed={datesQuery.data?.passed ?? []}
                  scheduleMissing={datesQuery.data?.scheduleMissing ?? false}
                  loading={datesQuery.isLoading}
                  value={deliveryDate}
                  onChange={setDeliveryDate}
                  hint={t('salesOrders:portal.cart.chooseDate')}
                />
              </div>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">{t('salesOrders:portal.cart.note')}</span>
                <Textarea
                  rows={2}
                  value={note}
                  onChange={(event) => {
                    setNote(event.target.value);
                  }}
                />
              </label>

              <Button
                disabled={!deliveryDate || (datesQuery.data?.scheduleMissing ?? false)}
                loading={createMutation.isPending}
                onClick={() => {
                  setConfirming(true);
                }}
              >
                {t('salesOrders:portal.cart.submit')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setQuantities({});
                  if (user) clearDraft(user.id, restaurantId);
                }}
              >
                {t('salesOrders:portal.cart.clear')}
              </Button>
            </>
          )}
        </Surface>
      </div>

      {confirming && deliveryDate ? (
        <ConfirmDialog
          title={t('salesOrders:portal.cart.confirmTitle')}
          message={t('salesOrders:portal.cart.confirmMessage', {
            lines: basket.length,
            date: deliveryDate,
            restaurant: restaurant?.name ?? '',
          })}
          confirmLabel={t('salesOrders:portal.cart.confirm')}
          cancelLabel={t('common:actions.cancel')}
          processingLabel={t('salesOrders:portal.cart.submitting')}
          onConfirm={async () => {
            await createMutation.mutateAsync(undefined);
          }}
          onClose={() => {
            setConfirming(false);
          }}
        />
      ) : null}
    </>
  );
}

function step(current: Record<string, string>, articleId: string, delta: number): Record<string, string> {
  const value = Number(current[articleId] ?? '0');
  const next = Math.max(0, (Number.isFinite(value) ? value : 0) + delta);
  if (next === 0) {
    const copy = { ...current };
    delete copy[articleId];
    return copy;
  }
  return { ...current, [articleId]: String(next) };
}

function trimQty(value: string): string {
  if (!value.includes('.')) return value;
  return value.replace(/\.?0+$/, '');
}
